/**
 * Git Integration Tab
 * Git 仓库集成管理组件 - 列表 + 详情 Dialog
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Alert } from '@material-ui/lab';
import { GitIntegrationList } from './GitIntegrationList';
import { GitIntegrationDetail } from './GitIntegrationDetail';
import { useGitIntegration } from './useGitIntegration';
import { IntegrationCheckResult } from './types';

export const GitIntegrationTab: React.FC = () => {
  const {
    integrations,
    loading,
    error,
    checkResult,
    checkResults,
    checkingHost,
    fetchIntegrations,
    checkIntegration,
    getCheckResult,
  } = useGitIntegration();

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailResult, setDetailResult] = useState<IntegrationCheckResult | null>(null);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // 检测完成后同步更新 detailResult
  useEffect(() => {
    if (checkResult && detailOpen) {
      setDetailResult(checkResult);
    }
  }, [checkResult, detailOpen]);

  const handleCheck = async (host: string) => {
    await checkIntegration(host, true);
  };

  const handleViewDetail = (host: string) => {
    const cachedResult = getCheckResult(host);
    if (cachedResult) {
      setDetailResult(cachedResult);
      setDetailOpen(true);
    } else {
      setDetailResult(null);
      setDetailOpen(true);
      checkIntegration(host, true);
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      <GitIntegrationList
        integrations={integrations}
        loading={loading}
        onRefresh={fetchIntegrations}
        onCheck={handleCheck}
        onViewDetail={handleViewDetail}
        checkingHost={checkingHost}
        checkResults={checkResults}
      />

      {/* 详情 Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="h6">
              🔗 Git 集成详情: {detailResult?.host || ''}
            </Typography>
            {detailResult?.gitlabVersion && (
              <Typography variant="caption" color="textSecondary">
                GitLab {detailResult.gitlabVersion.version}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => setDetailOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailResult ? (
            <GitIntegrationDetail result={detailResult} />
          ) : (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="textSecondary">加载中...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} color="primary">
            关闭
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
